/**
 * 에피소드 목록의 밴드 이탈 투영. 서버 값을 축 2칸이 그릴 모양으로 바꾼다(순수 계산).
 *
 * 서버가 축별 이탈률과 축별 최악 채널을 준다(`EpisodeBandDeviation`). 이 모듈은 그 값을
 * 심각도와 표시 문구에 필요한 모양으로만 바꾼다. 임계값의 SoT 는
 * `diagnostics/bandDeviation.ts` 이므로 여기서 수를 다시 정의하지 않는다.
 *
 * **"값 없음"과 "0%"를 구분한다.** 다음 두 경우는 잘 맞은 것이 아니라 잴 수 없는 것이다:
 *   - 그 축의 채널이 없는 데이터셋. `action/` 에 `done` 만 있는 직접교시 계열이
 *     prod 에피소드의 16.5% 다.
 *   - 아직 집계하지 않은 에피소드.
 * 두 경우 모두 `fraction === null` 이다. 목록은 그 칸을 회색으로 비워 "정상"으로 읽히지 않게 한다.
 *
 * **축을 나누는 이유**: "이 시연이 다른 시연과 다르게 **움직였나**(action)"와 "다른 시연과 다른
 * **상황을 봤나**(observation)"는 리뷰어에게 서로 다른 사실이다. 한 수로 합치면 그 구분이 사라진다.
 *
 * 진단 탭의 `bandDeviation.ts` 와 역할이 다르다. 그 모듈은 밴드·엔벨로프 본문을 받아 채널·dim
 * 별로 **다시 계산**한다. 이 모듈은 목록 응답에 포함된 값을 **표시용으로 바꾸기만** 한다.
 */

import type { EpisodeBandDeviation, EpisodeSummary } from "@/api";
import { deviationSeverity, type DeviationSeverity } from "./diagnostics/bandDeviation";

/** 축 하나가 목록에서 차지하는 칸. */
export type EpisodeBandAxisView = {
  // 이 축의 이탈률. 잴 수 없으면 null 이다. 0 과 구분한다.
  fraction: number | null;
  // 색 구간. 값이 없으면 null 이고, 칸은 회색으로 표시한다.
  severity: DeviationSeverity | null;
  // 이 축에서 가장 많이 벗어난 채널(`<topic>::<name>`)과 그 비율.
  worstChannel: string | null;
  worstFraction: number | null;
};

/** 한 행이 그릴 두 칸. */
export type EpisodeBandView = {
  action: EpisodeBandAxisView;
  observation: EpisodeBandAxisView;
};

const ABSENT: EpisodeBandAxisView = {
  fraction: null,
  severity: null,
  worstChannel: null,
  worstFraction: null,
};

/** 두 축을 모두 잴 수 없는 상태. 진단 전 에피소드가 이 상태다. */
export const ABSENT_BAND_VIEW: EpisodeBandView = {
  action: ABSENT,
  observation: ABSENT,
};

/**
 * 서버 밴드 값을 축 2칸으로 바꾼다.
 *
 * `band` 가 없으면(진단 전) 두 축을 모두 비운 뷰를 반환한다. 그래서 호출부는 null 을 따로
 * 처리하지 않고, 목록 행은 항상 같은 수의 칸을 그린다.
 */
export function toEpisodeBandView(band: EpisodeBandDeviation | null): EpisodeBandView {
  if (band === null) {
    return ABSENT_BAND_VIEW;
  }
  return {
    action: axisView(
      band.action_outside_fraction,
      band.action_worst_channel,
      band.action_worst_fraction,
    ),
    observation: axisView(
      band.observation_outside_fraction,
      band.observation_worst_channel,
      band.observation_worst_fraction,
    ),
  };
}

/** 목록 행의 밴드 뷰. `EpisodeSummary` 를 그대로 받는 편의 함수다. */
export function episodeBandView(episode: EpisodeSummary): EpisodeBandView {
  return toEpisodeBandView(episode.band ?? null);
}

function axisView(
  fraction: number | null,
  worstChannel: string | null,
  worstFraction: number | null,
): EpisodeBandAxisView {
  if (fraction === null || !Number.isFinite(fraction)) {
    return ABSENT;
  }
  return {
    fraction,
    severity: deviationSeverity(fraction),
    worstChannel,
    worstFraction,
  };
}
